import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Loader,
  LoadingOverlay,
  TextInput,
  Title,
  Text,
  Tooltip,
} from "@mantine/core";
import { useDebouncedState, useDisclosure, useHotkeys } from "@mantine/hooks";
import {
  IconFileTypeCsv,
  IconMail,
  IconPhone,
  IconSearch,
  IconTicket,
  IconUser,
} from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useDeferredValue, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { repositoryMdm, repositoryPos } from "../../../_base/_const/_constVar";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import { _localization } from "../../../config/location";
import { MessageResponse } from "../../../model/MessageResponse";
import { formatDateTransfer } from "../../../common/FormatDate/FormatDate";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { BASE_API_MDM } from "../../../config";

type ColumnPinningType = {
  left?: string[];
  right?: string[];
};

const VoucherUsageHistory = () => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<any[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [height, setHeight] = useState(0);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const rowSelectionDeferred =
    useDeferredValue<MRT_RowSelectionState>(rowSelection);
  const [selectIds, setSelectIds] = useState<string[]>([]);
  const [visible, { toggle, open, close }] = useDisclosure(false);

  const navigate = useNavigate();
  const location = useLocation();
  const _searchJson = sessionStorage.getItem(String(location.pathname))
    ? JSON.parse(sessionStorage.getItem(String(location.pathname)) ?? "")
    : null;
  const checkedPinSearch = localStorage.getItem("checkedPinSearch") == "true";
  const [search, setSearch] = useState<any>({});
  const [customerName, setCustomerName] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.customerName : "",
    300
  );
  const [phoneNumber, setPhoneNumber] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.phoneNumber : "",
    300
  );
  const [email, setEmail] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.email : "",
    300
  );
  const [voucherCode, setVoucherCode] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.voucherCode : "",
    300
  );
  const [keySearch, setKeySearch] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.keySearch : "",
    300
  );

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      { accessorKey: "customerName", header: "Khách hàng" },
      { accessorKey: "phoneNumber", header: "Số điện thoại" },
      {
        accessorKey: "email",
        header: "Email",
      },
      {
        accessorKey: "voucherCode",
        header: "Mã voucher",
        Cell: ({ renderedCellValue, row }) =>
          renderedCellValue ? (
            <Tooltip label="Xem chi tiết">
              <Badge
                variant="dot"
                color="green"
                onDoubleClick={() =>
                  handleCheckDetailVoucher(row.original.voucherId)
                }
              >
                {renderedCellValue}
              </Badge>
            </Tooltip>
          ) : (
            <></>
          ),
      },
      {
        accessorKey: "usedDate",
        header: "Ngày sử dụng",
        Cell: ({ renderedCellValue }: any) => (
          <Text size="12.5px" fw={"500"}>
            {renderedCellValue && formatDateTransfer(renderedCellValue)}
          </Text>
        ),
      },
      //   { accessorKey: "status", header: "Trạng thái" },
      //   { accessorKey: "attribute1", header: "Thuộc tính 1" },
      //   { accessorKey: "attribute2", header: "Thuộc tính 2" },
      {
        accessorKey: "attribute3",
        header: "Đơn hàng áp dụng voucher",
        size: 250,
        Cell: ({ renderedCellValue, row }) =>
          renderedCellValue ? (
            <Tooltip label="Xem chi tiết">
              <Badge
                variant="dot"
                color="blue"
                onDoubleClick={() => handleCheckView(row)}
              >
                {renderedCellValue}
              </Badge>
            </Tooltip>
          ) : (
            <></>
          ),
      },
    ],
    []
  );

  const handleCheckView = async (row: any) => {
    const url = `/api/v1/CreateSellItem/details?orderNumber=${row.original.attribute3}`;
    const response = await repositoryPos.get<MessageResponse<any>>(url);
    if (response && response.success) {
      // Kiểm tra nếu orderNumber chứa "DT"
      if (row.original.attribute3.includes("DT")) {
        navigate("/sell/view-order-return", {
          state: {
            id: row.original.id,
            orderNumber: row.original.attribute3,
            orderType: row.original.orderType,
            actionType: "view",
            // isReadOnly:
            //   location?.pathname === "/sell/sales-order-list-read-only"
            //     ? true
            //     : false,
          },
        });
      } else {
        navigate("/sell/view-order", {
          state: {
            id: row.original.id,
            orderNumber: row.original.attribute3,
            orderType: row.original.orderType,
            actionType: "view",
            // isReadOnly:
            //   location?.pathname === "/sell/sales-order-list-read-only"
            //     ? true
            //     : false,
          },
        });
      }
    }
  };

  const handleCheckDetailVoucher = (id: string | number) => {
    navigate("/category-data/detail-vourcher", {
      state: {
        id: id,
      },
    });
  };

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);
    try {
      let url = `/api/v1/TblVoucherUsage/get-list?Skip=${
        pagination.pageIndex * pagination.pageSize
      }&Take=${pagination.pageSize}`;

      const params = new URLSearchParams();
      Object.entries(search).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((item) => params.append(key, String(item)));
        } else if (typeof value === "string" && value.length > 0) {
          params.append(key, value);
        }
      });

      if (customerName.length > 0) {
        url += `&CustomerName=${customerName}`;
      }

      if (phoneNumber.length > 0) {
        url += `&PhoneNumber=${phoneNumber}`;
      }

      if (email.length > 0) {
        url += `&Email=${email}`;
      }

      if (voucherCode.length > 0) {
        url += `&VoucherCode=${voucherCode}`;
      }

      if (keySearch.length > 0) {
        url += `&KeySearch=${keySearch?.trim()}`;
      }

      url += `&${params.toString()}`;

      const res = await repositoryMdm.get<MessageResponse<any>>(url);

      if (res && res.success) {
        const result = res.data;
        setData(result);
        setRowCount(res.totalCount);
      } else {
        setData([]);
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
      sessionStorage.setItem(
        String(location.pathname),
        JSON.stringify({
          ...search,
          keySearch: keySearch,
          customerName: customerName,
          phoneNumber: phoneNumber,
          email: email,
          voucherCode: voucherCode,
        })
      );
    }
  };

  const exportDataExcel = async () => {
    open();
    try {
      let url = `?Skip=${pagination?.pageIndex}&Take=${pagination.pageSize}`;

      const params = new URLSearchParams();
      Object.entries(search).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((item) => params.append(key, String(item)));
        } else if (typeof value === "string" && value.length > 0) {
          params.append(key, value);
        }
      });

      if (customerName.length > 0) {
        url += `&CustomerName=${customerName}`;
      }

      if (phoneNumber.length > 0) {
        url += `&PhoneNumber=${phoneNumber}`;
      }

      if (email.length > 0) {
        url += `&Email=${email}`;
      }

      if (voucherCode.length > 0) {
        url += `&VoucherCode=${voucherCode}`;
      }

      if (keySearch.length > 0) {
        url += `&KeySearch=${keySearch?.trim()}`;
      }

      url += `&${params.toString()}`;

      const baseUrl = `/api/v1/ExportExcel/export-voucher-usage${url}`;

      const _url = BASE_API_MDM + baseUrl;
      const token = localStorage.getItem("token");

      const response = await fetch(_url, {
        method: "GET",
        headers: {
          "Content-Type": "application/vnd.ms-excel",
          Authorization: `Bearer ${token?.replace(`"`, "").replace(`"`, "")}`,
        },
      });

      if (!response.ok) {
        NotificationExtension.Fails("Export Excel thất bại !");
      } else {
        const blob = await response.blob();
        const urlss = window.URL.createObjectURL(blob);
        const _name = new Date().toISOString().slice(0, 10);
        let filename = "lich_su_su_dung_voucher.xlsx";
        const link = document.createElement("a");
        link.href = urlss;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        NotificationExtension.Success("Export Excel thành công !");
      }
    } catch (error) {
      console.log(error);
    } finally {
      close();
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination?.pageIndex, pagination?.pageSize]);

  const getColumnPinningStorage = () => {
    const columnPinningStorage = localStorage.getItem(
      "columnPinningVoucherUsageHistory"
    );
    return columnPinningStorage
      ? JSON.parse(columnPinningStorage)
      : {
          left: ["mrt-row-numbers", "voucherCode"],
          right: ["status", "action"],
        }; // Giá trị mặc định
  };

  // Lưu columnPinning vào state
  const [columnPinning, setColumnPinning] = useState<ColumnPinningType>(
    getColumnPinningStorage()
  );
  // Hàm lưu vào localStorage
  useEffect(() => {
    localStorage.setItem(
      "columnPinningVoucherUsageHistory",
      JSON.stringify(columnPinning)
    );
  }, [columnPinning]);

  useEffect(() => {
    //   startTransition(() => {
    const valuesList = Object.keys(rowSelectionDeferred);
    setSelectIds(valuesList);
    if (valuesList.length < 1) setSelectIds([]);
    else {
      const valuesList = Object.keys(rowSelectionDeferred);
      setSelectIds(valuesList);
    }
    //  });
  }, [rowSelectionDeferred]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (185 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnResizing: false,
    positionToolbarAlertBanner: "bottom",
    enableRowNumbers: true,
    //add custom action buttons to top-left of top toolbar
    enableTopToolbar: false,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    // enableRowSelection: true,
    getRowId: (row) => row.id?.toString(),
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        // left: ["mrt-row-numbers", "maVt"],
        // right: ["action"],
        ...columnPinning,
      },
      columnVisibility: { id: true, phone: false, fax: false },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    manualPagination: true,
    onColumnPinningChange: (newState) => {
      setColumnPinning(newState);
    },
    onRowSelectionChange: setRowSelection,
    rowCount: rowCount,
    onPaginationChange: setPagination,
    mantineTableBodyCellProps: ({ row }) => ({
      style: {
        fontWeight: "500", //conditional styling
        fontSize: "12.5px",
        padding: "5px 15px",
      },
    }),
    state: {
      isLoading,
      pagination: pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading, //loading for the first time with no data
      rowSelection,
      columnPinning,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    columnFilterDisplayMode: "popover",
    mantineTableBodyRowProps: ({ row }) => ({
      style: { cursor: "pointer" },
    }),
  });

  useHotkeys([
    [
      "Enter",
      () => {
        fetchData();
      },
    ],
  ]);

  return (
    <>
      <div
        ref={headerRef}
        style={{
          width: "100%",
        }}
      >
        <LoadingOverlay
          visible={visible}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 5 }}
          loaderProps={{
            children: (
              <Flex direction={"column"} align="center" gap="xs">
                <Loader type="dots" size={35}></Loader>
                {/* <Title size={15}> Đang upload tài liệu...</Title> */}
                <Title size={15}> Đang xử lý....</Title>
              </Flex>
            ),
          }}
        />
        <Flex justify={"space-between"} align={"center"} w={"100%"}>
          <_breadcrumb></_breadcrumb>
          <Flex gap={"md"}>
            <Button
              leftSection={<IconFileTypeCsv size={16} />}
              onClick={() => exportDataExcel()}
            >
              Export Excel
            </Button>
          </Flex>
        </Flex>
        <Grid w={"100%"}>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Nhập từ khóa tìm kiếm"
              defaultValue={keySearch}
              onChange={(e) => setKeySearch(e.currentTarget.value)}
              leftSection={<IconSearch color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Mã voucher"
              defaultValue={voucherCode}
              onChange={(e) => setVoucherCode(e.currentTarget.value)}
              leftSection={<IconTicket color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Khách hàng"
              defaultValue={customerName}
              onChange={(e) => setCustomerName(e.currentTarget.value)}
              leftSection={<IconUser color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="SĐT khách hàng"
              defaultValue={phoneNumber}
              onChange={(e) => setPhoneNumber(e.currentTarget.value)}
              leftSection={<IconPhone color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Email"
              defaultValue={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              leftSection={<IconMail color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Button
              onClick={() => fetchData()}
              leftSection={<IconSearch size={14} />}
            >
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>
      </div>
      <Box mt={10}>
        <MantineReactTable table={table} />
      </Box>
    </>
  );
};

export default VoucherUsageHistory;
